import {
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  computed,
  ViewEncapsulation,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  inject,
  Injector,
  afterNextRender,
  viewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import {
  CatalogueApiService,
  SearchCriteria,
  AclApdApiService,
  AuthApiService,
  FileApiService,
} from '@angular-workspace/services';

import {
  CanvasFilterMultiselectComponent,
  CanvasFilterOption,
} from './canvas-filter-multiselect/canvas-filter-multiselect.component';
import { CanvasBodyPortalService } from '../../canvas-body-portal.service';
import {
  CanvasDatasetsService,
  CanvasDatasetFilters,
} from '../../canvas-datasets.service';
import {
  CANVAS_OGC_QUERY_TYPES_SEARCH_CRITERION,
  CANVAS_QUERY_TYPE_OPTIONS,
  CanvasDataset,
  withCanvasPublishedCriterion,
} from '../../canvas-catalogue.util';
import {
  CANVAS_DATASET_DETAIL_URL,
  CANVAS_NOTIFIER,
  OGC_MAPS_RESOURCE_SERVER_URLS,
  OGC_TILES_COLLECTION_ID,
} from '../../../canvas-tokens';
import { isGrantedHasAccessResponse } from '../../normalize-acl-access-status';
import { isDatabankDownloadNotFoundError } from '../../canvas-download.util';
import { CanvasRequestAccessModalComponent } from './canvas-request-access-modal/canvas-request-access-modal.component';
import { KeycloakService } from 'keycloak-angular';
import { CanvasDatasetLoadButtonsComponent } from '../dataset-load/canvas-dataset-load-buttons.component';

/** Catalogue item type for geospatial / dataset bank listings (same as catalogue datasets page). */
const DEFAULT_CATALOGUE_TYPE = 'adex:DataBank';

@Component({
  selector: 'lib-canvas-datasets-modal',
  standalone: true,
  imports: [
    CommonModule,
    CanvasFilterMultiselectComponent,
    CanvasRequestAccessModalComponent,
    CanvasDatasetLoadButtonsComponent,
  ],
  templateUrl: './datasets-modal.component.html',
  styleUrl: './datasets-modal.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class DatasetsModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  /** Optional override for `getFilterList` search criterion `type` values (default DataBank). */
  @Input() catalogueAssetType = DEFAULT_CATALOGUE_TYPE;
  @Output() closeModal = new EventEmitter<void>();
  /** Global bbox search across OGC record collections. */
  @Output() startBboxSearch = new EventEmitter<void>();
  /** Per-dataset bbox filter on the main map. */
  @Output() startBboxFilter = new EventEmitter<CanvasDataset>();
  /** Load full dataset onto the main map. */
  @Output() startGetAll = new EventEmitter<CanvasDataset>();
  /** Open attribute filter dialog for this dataset. */
  @Output() startAttrFilter = new EventEmitter<CanvasDataset>();
  @Output() startStacList = new EventEmitter<CanvasDataset>();
  @Output() startLoadTiles = new EventEmitter<CanvasDataset>();

  static readonly API_TYPE_OPTIONS: CanvasFilterOption[] = [
    ...CANVAS_QUERY_TYPE_OPTIONS,
  ];

  private readonly catalogueApi = inject(CatalogueApiService);
  private readonly injector = inject(Injector);
  private readonly bodyPortal = inject(CanvasBodyPortalService);
  private readonly dmPortalRoot =
    viewChild<ElementRef<HTMLElement>>('dmPortal');
  private readonly datasetsService = inject(CanvasDatasetsService);
  private readonly ogcServerUrls =
    inject(OGC_MAPS_RESOURCE_SERVER_URLS, { optional: true }) ?? [];
  private readonly aclApdApi = inject(AclApdApiService);
  private readonly authApi = inject(AuthApiService);
  private readonly fileApi = inject(FileApiService);
  private readonly keycloak = inject(KeycloakService);
  private readonly datasetDetailUrl =
    inject(CANVAS_DATASET_DETAIL_URL, { optional: true }) ??
    ((id: string) => `/dataset/${id}`);
  private readonly notifier = inject(CANVAS_NOTIFIER, { optional: true });
  private portalDetach?: () => void;

  /** Hides geospatial panel while the request-access modal is shown. */
  geospatialPanelVisible = signal(true);

  /** Live datasets from the OGC resource server — backed by CanvasDatasetsService. */
  readonly datasets = this.datasetsService.datasets;
  readonly loading = this.datasetsService.loading;
  readonly error = this.datasetsService.error;
  readonly searchQuery = this.datasetsService.searchQuery;

  /** Dataset ids currently showing the no-access download message. */
  noAccessDatasetIds = signal<Set<string>>(new Set());
  /** Dataset ids where download API returned file not found. */
  fileNotFoundDatasetIds = signal<Set<string>>(new Set());
  /** Dataset ids currently showing the no-access Get All message. */
  getAllNoAccessDatasetIds = signal<Set<string>>(new Set());
  /** Dataset id with an in-flight download/access check. */
  downloadBusyId = signal<string | null>(null);

  showRequestAccessModal = signal(false);
  requestAccessDatasetId = signal<string | null>(null);
  userHasOrganisation = signal(false);
  organisationName = signal('');

  expandedIds = signal<Set<string>>(new Set());
  isFilterPanelOpen = signal(false);
  /** At most one filter multiselect panel open at a time. */
  openFilterMultiselectKey = signal<string | null>(null);

  organisationOptions = signal<string[]>([]);
  tagsOptions = signal<string[]>([]);
  typesOptions = signal<string[]>([]);
  /** Access policy rows with display labels (Public / Restricted / …). */
  accessPolicyOptions = signal<{ label: string; value: string }[]>([]);
  crsOptions = signal<string[]>([]);
  themesOptions = signal<string[]>([]);

  filtersLoading = signal(false);
  private filtersLoadedForSession = false;

  selectedOrganisations = signal<string[]>([]);
  selectedTags = signal<string[]>([]);
  selectedTypes = signal<string[]>([]);
  selectedAccessPolicies = signal<string[]>([]);
  selectedCrs = signal<string[]>([]);
  selectedThemes = signal<string[]>([]);
  selectedApiTypes = signal<string[]>([]);

  readonly apiTypeOptionsModel = DatasetsModalComponent.API_TYPE_OPTIONS;

  organisationOptionsModel = computed<CanvasFilterOption[]>(() =>
    this.organisationOptions().map((o) => ({ label: o, value: o })),
  );
  tagsOptionsModel = computed<CanvasFilterOption[]>(() =>
    this.tagsOptions().map((o) => ({ label: o, value: o })),
  );
  typesOptionsModel = computed<CanvasFilterOption[]>(() =>
    this.typesOptions().map((o) => ({ label: o, value: o })),
  );
  accessPolicyOptionsModel = computed<CanvasFilterOption[]>(() =>
    this.accessPolicyOptions().map((o) => ({ label: o.label, value: o.value })),
  );
  crsOptionsModel = computed<CanvasFilterOption[]>(() =>
    this.crsOptions().map((o) => ({ label: o, value: o })),
  );
  themesOptionsModel = computed<CanvasFilterOption[]>(() =>
    this.themesOptions().map((o) => ({ label: o, value: o })),
  );

  hasActiveFilters = computed(
    () =>
      this.selectedOrganisations().length > 0 ||
      this.selectedTags().length > 0 ||
      this.selectedTypes().length > 0 ||
      this.selectedAccessPolicies().length > 0 ||
      this.selectedCrs().length > 0 ||
      this.selectedThemes().length > 0 ||
      this.selectedApiTypes().length > 0,
  );

  onSearchInput(event: Event): void {
    this.collapseExpandedCards();
    this.datasetsService.searchDatasets(
      (event.target as HTMLInputElement).value,
    );
  }

  onOrganisationsFilterChange(values: string[]): void {
    this.selectedOrganisations.set(values);
    this.applyFiltersToSearch();
  }

  onTagsFilterChange(values: string[]): void {
    this.selectedTags.set(values);
    this.applyFiltersToSearch();
  }

  onTypesFilterChange(values: string[]): void {
    this.selectedTypes.set(values);
    this.applyFiltersToSearch();
  }

  onAccessPoliciesFilterChange(values: string[]): void {
    this.selectedAccessPolicies.set(values);
    this.applyFiltersToSearch();
  }

  onCrsFilterChange(values: string[]): void {
    this.selectedCrs.set(values);
    this.applyFiltersToSearch();
  }

  onThemesFilterChange(values: string[]): void {
    this.selectedThemes.set(values);
    this.applyFiltersToSearch();
  }

  onApiTypesFilterChange(values: string[]): void {
    this.selectedApiTypes.set(values);
    this.applyFiltersToSearch();
  }

  clearAllFilters(): void {
    if (!this.hasActiveFilters()) {
      return;
    }
    this.openFilterMultiselectKey.set(null);
    this.clearFilterSelections();
    this.applyFiltersToSearch();
  }

  private applyFiltersToSearch(): void {
    this.collapseExpandedCards();
    const filters: CanvasDatasetFilters = {
      organisations: this.selectedOrganisations(),
      tags: this.selectedTags(),
      organizationTypes: this.selectedTypes(),
      accessPolicies: this.selectedAccessPolicies(),
      crs: this.selectedCrs(),
      themes: this.selectedThemes(),
      apiTypes: this.selectedApiTypes(),
    };
    this.datasetsService.applyFilters(filters);
  }

  onStacListClick(dataset: CanvasDataset): void {
    void this.runRestrictedDatasetAction(dataset, () => {
      this.startStacList.emit(dataset);
    });
  }

  onLoadTilesClick(dataset: CanvasDataset): void {
    void this.runRestrictedDatasetAction(dataset, () => {
      this.startLoadTiles.emit(dataset);
    });
  }

  /** Base catalogue criteria scoped to OGC resource server datasets. */
  private buildOgcScopedSearchCriteria(): SearchCriteria[] {
    return withCanvasPublishedCriterion([
      { searchType: 'term', field: 'type', values: [this.catalogueAssetType] },
      ...this.buildOgcResourceServerCriteria(),
      CANVAS_OGC_QUERY_TYPES_SEARCH_CRITERION,
    ]);
  }

  private buildOgcResourceServerCriteria(): SearchCriteria[] {
    const ogcUrls = this.ogcServerUrls.map((u) => u.trim()).filter(Boolean);
    if (ogcUrls.length === 0) {
      return [];
    }
    return [
      {
        searchType: 'flattenedTerm',
        field: 'resourceServer.url',
        values: ogcUrls,
      },
    ];
  }

  openDatasetDetail(datasetId: string): void {
    const url = this.datasetDetailUrl(datasetId);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  isDownloadBusy(datasetId: string): boolean {
    return this.downloadBusyId() === datasetId;
  }

  showsNoAccessMessage(datasetId: string): boolean {
    return this.noAccessDatasetIds().has(datasetId);
  }

  showsFileNotFoundMessage(datasetId: string): boolean {
    return this.fileNotFoundDatasetIds().has(datasetId);
  }

  showsGetAllNoAccessMessage(datasetId: string): boolean {
    return this.getAllNoAccessDatasetIds().has(datasetId);
  }

  async onDownloadDataset(dataset: CanvasDataset): Promise<void> {
    if (this.downloadBusyId()) {
      return;
    }

    this.downloadBusyId.set(dataset.id);
    this.clearNoAccessMessage(dataset.id);
    this.clearFileNotFoundMessage(dataset.id);

    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.setNoAccessMessage(dataset.id);
        return;
      }

      const hasAccess = await this.userHasDatasetAccess(dataset);
      if (!hasAccess) {
        this.setNoAccessMessage(dataset.id);
        return;
      }

      await this.triggerDatasetDownload(dataset.id);
    } catch (err: unknown) {
      if (isDatabankDownloadNotFoundError(err)) {
        this.setFileNotFoundMessage(dataset.id);
      } else {
        this.notifier?.error('Failed to download file.');
      }
    } finally {
      this.downloadBusyId.set(null);
    }
  }

  async onRequestAccessClick(datasetId: string): Promise<void> {
    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        await this.keycloak.login({ redirectUri: window.location.href });
        return;
      }

      this.requestAccessDatasetId.set(datasetId);
      await this.loadUserOrganisation();
      this.geospatialPanelVisible.set(false);
      this.showRequestAccessModal.set(true);
    } catch {
      // Login redirect or profile load failed — user can retry.
    }
  }

  onRequestAccessModalVisibleChange(visible: boolean): void {
    this.showRequestAccessModal.set(visible);
    if (!visible) {
      this.requestAccessDatasetId.set(null);
      this.geospatialPanelVisible.set(true);
      this.collapseExpandedCards();
    }
  }

  async onRequestAccessSubmit(formData: {
    requestAs: string;
    purposeDescription: string;
    agreeToTerms: boolean;
    organisationName?: string;
  }): Promise<void> {
    const datasetId = this.requestAccessDatasetId();
    if (!datasetId) {
      return;
    }

    try {
      const payload = {
        itemId: datasetId,
        requestType: 'DOWNLOAD',
        additionalInfo: {
          requestAs: formData.requestAs,
          purposeOfRequest: formData.purposeDescription,
          organisationName:
            formData.requestAs === 'Organisation'
              ? (formData.organisationName ?? '')
              : '',
        },
      };

      await this.aclApdApi.createAccessRequest(payload);
      this.showRequestAccessModal.set(false);
      this.requestAccessDatasetId.set(null);
      this.clearNoAccessMessage(datasetId);
      this.clearGetAllNoAccessMessage(datasetId);
      this.notifier?.success('Your request is sent, Successfully');
      this.geospatialPanelVisible.set(true);
      this.collapseExpandedCards();
    } catch {
      this.notifier?.error('Failed to send access request. Please try again.');
    }
  }

  private async userHasDatasetAccess(dataset: CanvasDataset): Promise<boolean> {
    if (this.isOpenAccessPolicy(dataset.accessPolicy)) {
      return true;
    }

    try {
      const response = await this.aclApdApi.hasAccess({ itemId: dataset.id });
      return isGrantedHasAccessResponse(response, true);
    } catch (err: unknown) {
      return isGrantedHasAccessResponse(err, false);
    }
  }

  /** Login + has_access for restricted datasets. Keeps the modal open on deny. */
  private async runRestrictedDatasetAction(
    dataset: CanvasDataset,
    onAllowed: () => void,
  ): Promise<void> {
    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.setGetAllNoAccessMessage(dataset.id);
        return;
      }

      const hasAccess = await this.userHasDatasetAccess(dataset);
      if (!hasAccess) {
        this.setGetAllNoAccessMessage(dataset.id);
        return;
      }

      this.clearGetAllNoAccessMessage(dataset.id);
      onAllowed();
    } catch {
      // Access check failed — user can retry.
    }
  }

  private async triggerDatasetDownload(datasetId: string): Promise<void> {
    const { downloadUrl } =
      await this.fileApi.getDatabankDownloadUrl(datasetId);
    if (!downloadUrl) {
      throw new Error('No download URL received.');
    }

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `${datasetId}_databank.zip`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private isOpenAccessPolicy(accessPolicy: string): boolean {
    return accessPolicy.trim().toUpperCase() === 'OPEN';
  }

  private setNoAccessMessage(datasetId: string): void {
    this.clearFileNotFoundMessage(datasetId);
    this.noAccessDatasetIds.update((set) => {
      const next = new Set(set);
      next.add(datasetId);
      return next;
    });
  }

  private clearNoAccessMessage(datasetId: string): void {
    this.noAccessDatasetIds.update((set) => {
      if (!set.has(datasetId)) {
        return set;
      }
      const next = new Set(set);
      next.delete(datasetId);
      return next;
    });
  }

  private setFileNotFoundMessage(datasetId: string): void {
    this.clearNoAccessMessage(datasetId);
    this.fileNotFoundDatasetIds.update((set) => {
      const next = new Set(set);
      next.add(datasetId);
      return next;
    });
  }

  private clearFileNotFoundMessage(datasetId: string): void {
    this.fileNotFoundDatasetIds.update((set) => {
      if (!set.has(datasetId)) {
        return set;
      }
      const next = new Set(set);
      next.delete(datasetId);
      return next;
    });
  }

  private setGetAllNoAccessMessage(datasetId: string): void {
    this.getAllNoAccessDatasetIds.update((set) => {
      const next = new Set(set);
      next.add(datasetId);
      return next;
    });
  }

  private clearGetAllNoAccessMessage(datasetId: string): void {
    this.getAllNoAccessDatasetIds.update((set) => {
      if (!set.has(datasetId)) {
        return set;
      }
      const next = new Set(set);
      next.delete(datasetId);
      return next;
    });
  }

  private async loadUserOrganisation(): Promise<void> {
    try {
      const userData = await this.authApi.getUserInfo();
      const result = userData?.['result'] as
        | Record<string, unknown>
        | undefined;
      if (!result) {
        this.userHasOrganisation.set(false);
        this.organisationName.set('');
        return;
      }

      let organisationName: string | null = null;
      let organisationStatus: string | null = null;
      const organisation = result['organisation'] as
        | Record<string, unknown>
        | undefined;

      if (organisation) {
        const rawStatus = organisation['status'];
        organisationStatus = rawStatus
          ? String(rawStatus).toLowerCase().trim()
          : null;
        if (organisationStatus !== 'pending') {
          organisationName =
            String(
              organisation['organisationName'] ?? organisation['name'] ?? '',
            ).trim() || null;
        }
      }

      if (!organisationName && organisationStatus !== 'pending') {
        const organisationId =
          organisation && organisation['organisationId']
            ? String(organisation['organisationId'])
            : null;

        if (organisationId) {
          const organisationsResponse = await this.authApi.getAllOrganisations(
            1,
            100,
          );
          let organisations: Record<string, unknown>[] = [];
          if (Array.isArray(organisationsResponse)) {
            organisations = organisationsResponse as Record<string, unknown>[];
          } else if (organisationsResponse?.['result']) {
            const resultValue = organisationsResponse['result'];
            organisations = Array.isArray(resultValue)
              ? (resultValue as Record<string, unknown>[])
              : (((resultValue as Record<string, unknown>)[
                  'organisations'
                ] as Record<string, unknown>[]) ?? []);
          }

          const matched = organisations.find((org) => {
            const orgId =
              org['id'] ?? org['organisationId'] ?? org['organisation_id'];
            return String(orgId) === organisationId;
          });

          if (matched) {
            organisationName =
              String(
                matched['orgName'] ??
                  matched['name'] ??
                  matched['organisationName'] ??
                  '',
              ).trim() || null;
          }
        }
      }

      if (organisationName && organisationStatus !== 'pending') {
        this.organisationName.set(organisationName);
        this.userHasOrganisation.set(true);
      } else {
        this.organisationName.set('');
        this.userHasOrganisation.set(false);
      }
    } catch {
      this.organisationName.set('');
      this.userHasOrganisation.set(false);
    }
  }

  toggleExpand(id: string): void {
    this.expandedIds.update((set) => {
      if (set.has(id)) {
        return new Set();
      }
      return new Set([id]);
    });
  }

  isExpanded(id: string): boolean {
    return this.expandedIds().has(id);
  }

  private collapseExpandedCards(): void {
    this.expandedIds.set(new Set());
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('cv-dm-backdrop')) {
      this.closeModal.emit();
    }
  }

  onBboxSearchClick(): void {
    this.startBboxSearch.emit();
    this.closeModal.emit();
  }

  onBboxFilterClick(dataset: CanvasDataset): void {
    void this.runRestrictedDatasetAction(dataset, () => {
      this.startBboxFilter.emit(dataset);
      this.closeModal.emit();
    });
  }

  onGetAllClick(dataset: CanvasDataset): void {
    void this.runRestrictedDatasetAction(dataset, () => {
      this.startGetAll.emit(dataset);
      this.closeModal.emit();
    });
  }

  onAttrFilterClick(dataset: CanvasDataset): void {
    void this.runRestrictedDatasetAction(dataset, () => {
      this.startAttrFilter.emit(dataset);
      this.closeModal.emit();
    });
  }

  onFilterMultiselectExclusiveChange(ev: { key: string; open: boolean }): void {
    if (ev.open) {
      this.openFilterMultiselectKey.set(ev.key);
    } else if (this.openFilterMultiselectKey() === ev.key) {
      this.openFilterMultiselectKey.set(null);
    }
  }

  async toggleFilterPanel(): Promise<void> {
    this.collapseExpandedCards();
    const next = !this.isFilterPanelOpen();
    if (!next) {
      this.openFilterMultiselectKey.set(null);
    }
    this.isFilterPanelOpen.set(next);
    if (next && !this.filtersLoadedForSession) {
      await this.loadCatalogueFilters();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const isOpenCh = changes['isOpen'];
    if (isOpenCh) {
      if (isOpenCh.currentValue === true) {
        this.expandedIds.set(new Set());
        this.geospatialPanelVisible.set(true);
        void this.datasetsService.loadOgcDatasets(this.ogcServerUrls);
        if (!this.filtersLoadedForSession) {
          void this.loadCatalogueFilters();
        }
        afterNextRender(
          () => {
            if (!this.isOpen) return;
            const root = this.dmPortalRoot()?.nativeElement;
            if (!root?.parentElement) return;
            this.portalDetach?.();
            this.portalDetach = this.bodyPortal.attach(root);
          },
          { injector: this.injector },
        );
      } else {
        this.detachModalPortal();
      }
    }
    if (changes['isOpen'] && !this.isOpen) {
      this.expandedIds.set(new Set());
      this.isFilterPanelOpen.set(false);
      this.openFilterMultiselectKey.set(null);
      this.noAccessDatasetIds.set(new Set());
      this.fileNotFoundDatasetIds.set(new Set());
      this.getAllNoAccessDatasetIds.set(new Set());
      this.downloadBusyId.set(null);
      this.showRequestAccessModal.set(false);
      this.requestAccessDatasetId.set(null);
      this.geospatialPanelVisible.set(true);
      this.resetFilterOptions();
    }
    if (
      changes['catalogueAssetType'] &&
      !changes['catalogueAssetType'].firstChange
    ) {
      this.filtersLoadedForSession = false;
      if (this.isFilterPanelOpen()) {
        void this.loadCatalogueFilters();
      }
    }
  }

  private resetFilterOptions(): void {
    this.filtersLoadedForSession = false;
    this.organisationOptions.set([]);
    this.tagsOptions.set([]);
    this.typesOptions.set([]);
    this.accessPolicyOptions.set([]);
    this.crsOptions.set([]);
    this.themesOptions.set([]);
    this.clearFilterSelections();
  }

  private clearFilterSelections(): void {
    this.selectedOrganisations.set([]);
    this.selectedTags.set([]);
    this.selectedTypes.set([]);
    this.selectedApiTypes.set([]);
    this.selectedAccessPolicies.set([]);
    this.selectedCrs.set([]);
    this.selectedThemes.set([]);
  }

  /**
   * POST `{API_BASE_URL}{CAT_API_PATH}list` → `/iudx/v2/cat/list` when apps provide `CAT_API_PATH`.
   */
  private async loadCatalogueFilters(): Promise<void> {
    this.filtersLoading.set(true);
    try {
      const filterFields = [
        'organization',
        'tags',
        'organizationType',
        'accessPolicy',
        'concepts',
        'resourceServer.url',
        'crs',
      ];

      const response = await this.catalogueApi.getFilterList(
        this.buildOgcScopedSearchCriteria(),
        filterFields,
        {
          isLoaderRequired: false,
          isToasterRequired: true,
        },
      );

      const apiData = this.normalizeCatalogListResult(response);
      if (!apiData) {
        return;
      }

      this.organisationOptions.set(
        this.extractScalarList(apiData, 'organization'),
      );
      this.tagsOptions.set(this.extractScalarList(apiData, 'tags'));
      this.typesOptions.set(
        this.extractScalarList(apiData, 'organizationType'),
      );
      this.themesOptions.set(this.extractScalarList(apiData, 'concepts'));

      const crsFromField = this.extractScalarList(apiData, 'crs');
      this.crsOptions.set(crsFromField);

      const rawPolicies = this.extractScalarList(apiData, 'accessPolicy');
      const labelMap: Record<string, string> = {
        open: 'Public',
        restricted: 'Restricted',
        pii: 'Personal',
      };
      this.accessPolicyOptions.set(
        rawPolicies.map((v) => ({
          value: v,
          label: labelMap[String(v).toLowerCase()] ?? v,
        })),
      );

      this.filtersLoadedForSession = true;
    } catch {
      // Errors surfaced via NetworkService toaster when enabled
    } finally {
      this.filtersLoading.set(false);
    }
  }

  /** Merge catalogue `list` API result shapes (array of buckets vs single object). */
  private normalizeCatalogListResult(
    response: unknown,
  ): Record<string, unknown> | null {
    if (!response || typeof response !== 'object') return null;
    const r = response as Record<string, unknown>;
    const result = r['result'];
    if (result == null) return null;

    if (
      Array.isArray(result) &&
      result.length > 0 &&
      typeof result[0] === 'object'
    ) {
      const merged: Record<string, unknown[]> = {};
      for (const item of result as Record<string, unknown>[]) {
        for (const [key, val] of Object.entries(item)) {
          if (!merged[key]) merged[key] = [];
          if (Array.isArray(val)) {
            for (const x of val) {
              if (x != null && x !== '') merged[key].push(x);
            }
          } else if (val != null && val !== '') {
            merged[key].push(val);
          }
        }
      }
      const deduped: Record<string, unknown> = {};
      for (const [key, vals] of Object.entries(merged)) {
        deduped[key] = [...new Set(vals.map(String))];
      }
      return deduped;
    }

    if (typeof result === 'object' && !Array.isArray(result)) {
      return result as Record<string, unknown>;
    }

    return null;
  }

  private extractScalarList(
    data: Record<string, unknown>,
    field: string,
  ): string[] {
    const parts = field.split('.');
    let cur: unknown = data;
    for (const p of parts) {
      cur = (cur as Record<string, unknown>)?.[p];
    }

    if (Array.isArray(cur)) {
      return [...new Set(cur.filter((x) => x != null && x !== '').map(String))];
    }
    if (cur != null && cur !== '') {
      return [String(cur)];
    }
    return [];
  }

  private detachModalPortal(): void {
    this.portalDetach?.();
    this.portalDetach = undefined;
  }

  ngOnDestroy(): void {
    this.detachModalPortal();
  }
}
